import { intersection, without } from 'lodash';
import { Column, RowData } from './types';
import { FocusableCheckboxIcon } from '@ardoq/forms';
import styled from 'styled-components';
import { insetFocusRingCSS } from '@ardoq/style-helpers';
import { colors } from '@ardoq/design-tokens';

type CheckboxColumnConfigOptions = {
  onSelectAll: () => void;
  onSelectRow: (rowId: string) => void;
  selectableIds: string[];
  selectedIds: string[];
  dataIndex: string;
};

const InvisibleButton = styled.button`
  padding: 0;
  margin: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  &:focus-visible {
    ${insetFocusRingCSS};
  }
`;

const getCheckboxColumnConfig = <TData extends RowData>({
  onSelectAll,
  onSelectRow,
  selectableIds,
  selectedIds,
  dataIndex,
}: CheckboxColumnConfigOptions): Column<TData> => ({
  dataIndex,
  headerStyle: { width: 24 },
  headerRender: () => (
    <InvisibleButton onClick={onSelectAll}>
      <FocusableCheckboxIcon
        isChecked={selectableIds.length === selectedIds.length}
        isMixed={
          selectedIds.length > 0 && selectableIds.length !== selectedIds.length
        }
      />
    </InvisibleButton>
  ),
  valueRender: id => {
    if (!id) return null;
    return (
      <InvisibleButton
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
          onSelectRow(id as string);
        }}
      >
        <FocusableCheckboxIcon isChecked={selectedIds.includes(id as string)} />
      </InvisibleButton>
    );
  },
});

type UseSelectableTableOptions = {
  selectableIds: string[];
  selectedIds: string[];
  setSelectedIds: (selectedIds: string[]) => void;
  dataIndex?: string;
};

export const useSelectableTable = ({
  selectableIds,
  selectedIds,
  setSelectedIds,
  dataIndex = 'id',
}: UseSelectableTableOptions) => {
  const sanitizedSelectedIds = intersection(selectedIds, selectableIds);
  const isAllSelected = selectableIds.length === sanitizedSelectedIds.length;
  return {
    rowStyle: (rowData: any) =>
      selectedIds.includes(rowData.id)
        ? { background: colors.surfaceInfo }
        : {},
    checkboxColumnConfig: getCheckboxColumnConfig({
      onSelectAll: () =>
        isAllSelected ? setSelectedIds([]) : setSelectedIds(selectableIds),
      onSelectRow: rowId => {
        setSelectedIds(
          sanitizedSelectedIds.includes(rowId)
            ? without(sanitizedSelectedIds, rowId)
            : [...sanitizedSelectedIds, rowId]
        );
      },
      selectedIds: sanitizedSelectedIds,
      selectableIds,
      dataIndex,
    }),
  };
};
